// API Base URL — always relative. In production the API is same-origin
// (Express serves dist/). In dev, the Parcel proxy (.proxyrc.js) forwards
// /api/* to localhost:5000. An absolute URL breaks any browser where
// localhost isn't the API host (e.g. colleagues on the LAN).
const API_BASE = '/api';

// Default request timeout (ms) — overridable per call via options.timeout
const DEFAULT_TIMEOUT_MS = 30000;

// Active hospital for global admin. When the global admin selects a hospital
// via the header dropdown, this is set and every apiCall includes it as the
// X-Hospital-Id header so the backend scopes to that hospital.
// Per-hospital users (hospitalId set in their JWT) don't need this — the
// backend reads hospital_id from the token.
let activeHospitalId = null;

export const setActiveHospitalId = (id) => {
  activeHospitalId = id;
  try {
    if (id) localStorage.setItem('lab_inventory_active_hospital', id);
    else localStorage.removeItem('lab_inventory_active_hospital');
  } catch {}
};

export const getActiveHospitalId = () => {
  if (!activeHospitalId) {
    try { activeHospitalId = localStorage.getItem('lab_inventory_active_hospital') || null; } catch {}
  }
  return activeHospitalId;
};

// Sessions are cookie-based: the server sets an httpOnly token cookie and the
// browser sends it automatically. This helper remains as a cleanup path for
// tokens persisted by older builds, and for clearing per-tab hospital state.
export const clearAuthToken = () => {
  activeHospitalId = null;
  try { localStorage.removeItem('lab_inventory_token'); } catch {}
  try { localStorage.removeItem('lab_inventory_active_hospital'); } catch {}
};

// Utility function for API calls
export const apiCall = async (endpoint, options = {}) => {
  const isFormData = options.body instanceof FormData;
  const headers = {
    ...options.headers,
  };

  // Only set Content-Type for non-FormData requests
  // FormData needs the browser to set the boundary automatically
  if (!isFormData) {
    headers["Content-Type"] = "application/json";
  }

  // Inject the active hospital for global admin. Per-hospital users have
  // hospitalId in their JWT; they don't send this header.
  const hid = getActiveHospitalId();
  if (hid) {
    headers["X-Hospital-Id"] = hid;
  }

  const timeoutMs = options.timeout ?? DEFAULT_TIMEOUT_MS;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  let response;
  try {
    response = await fetch(`${API_BASE}${endpoint}`, {
      cache: "no-store",
      credentials: "same-origin",
      ...options,
      headers,
      signal: controller.signal,
    });
  } catch (err) {
    clearTimeout(timeoutId);
    if (err?.name === "AbortError") {
      throw new Error(`Request timed out after ${timeoutMs}ms: ${endpoint}`);
    }
    throw new Error(`Network error: ${err?.message || "fetch failed"} (${endpoint})`);
  }
  clearTimeout(timeoutId);

  if (response.status === 401) {
    // Login endpoint returns 401 for bad credentials — surface that real
    // error ("Invalid credentials") instead of the misleading "Session expired".
    // Only treat 401 as session-expiry for authenticated endpoints (login is /auth/login).
    const isLoginEndpoint = endpoint === '/auth/login' || endpoint === '/auth/forgot-password' || endpoint === '/auth/reset-password';
    if (isLoginEndpoint) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Invalid credentials');
    }
    clearAuthToken();
    // Tell AuthProvider the session died, wherever the failing call came from.
    // Without this, only AppContent's initial fetch reacted to an expiry; a 401
    // from a save/scan/config action left the UI rendering as logged-in while
    // every subsequent request failed ("zombie session").
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('auth:expired'));
    }
    const err = new Error('Session expired. Please log in again.');
    err.status = 401;
    throw err;
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    // Surface validation details when present (e.g. Joi failures return
    // { error: 'Validation failed', details: [{ field, message }] }). Without
    // this the user only sees "Validation failed" with no clue which field.
    const detailMsg = Array.isArray(errorData.details) && errorData.details.length
      ? errorData.details.map(d => `${d.field}: ${d.message}`).join('; ')
      : '';
    const message = detailMsg
      ? `${errorData.error || 'Validation failed'} — ${detailMsg}`
      : (errorData.error || `HTTP error! status: ${response.status}`);
    const err = new Error(message);
    err.details = errorData.details || undefined;
    err.status = response.status;
    throw err;
  }

  // Support blob responses for file downloads
  if (options.responseType === "blob") {
    return await response.blob();
  }

  return await response.json().catch(() => {
    throw new Error(`Server returned an invalid response (status: ${response.status}).`);
  });
};
